import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { ImagePlus, Palette } from "lucide-react";
import api, { formatApiError, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { PLACEMENTS } from "./CreateBannerModal";

function ColorField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-xs font-bold text-brand-textMuted mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testId}-picker`}
          className="w-10 h-10 rounded-lg border border-brand-border cursor-pointer"
        />
        <Input
          data-testid={testId}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="bg-brand-bg border-brand-border rounded-xl h-10 font-mono text-sm"
        />
      </div>
    </div>
  );
}

const DEFAULTS = {
  enabled: true, badge: "", title: "", highlight: "", description: "",
  button_label: "", button_link: "", secondary_label: "", secondary_link: "",
  image_path: null, bg_color: "#1C2321", text_color: "#FFFFFF",
  button_bg: "#F4B942", button_text: "#1C2321", accent_color: "#F4B942",
};

export default function EditBannerModal({ open, banner, onClose, onSaved }) {
  const [form, setForm] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (banner) setForm({ ...DEFAULTS, ...banner });
  }, [banner]);

  if (!banner) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_path", data.path);
      toast.success("Imagem enviada");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, image_path: form.image_path == null ? "" : form.image_path };
      const { data } = await api.put(`/moderation/banners/${banner.banner_id}`, payload);
      toast.success("Banner atualizado");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="edit-banner-modal" className="max-w-3xl max-h-[92vh] overflow-y-auto bg-white rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Editar banner: {banner.banner_id === "hero" ? "Home (Hero)" : "Abaixo-assinado"}</DialogTitle>
          <DialogDescription className="text-brand-textMuted">
            Ajuste texto, link, cores e imagem. Desative se quiser esconder o banner do site.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={save} className="space-y-6 mt-2">
          {/* Preview */}
          <div className="rounded-2xl overflow-hidden border border-brand-border" style={{ background: form.bg_color, color: form.text_color }}>
            <div className="p-6 sm:p-8">
              {form.badge && (
                <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.18em]" style={{ background: `${form.accent_color}22`, color: form.text_color, border: `1px solid ${form.accent_color}55` }}>
                  {form.badge}
                </span>
              )}
              <h3 className="font-heading font-black text-2xl sm:text-3xl mt-3">
                {form.title} {form.highlight && <span style={{ color: form.accent_color }}>{form.highlight}</span>}
              </h3>
              <p className="mt-2 opacity-90 text-sm">{form.description}</p>
              <div className="mt-4 flex gap-2 flex-wrap">
                {form.button_label && (
                  <span className="inline-block rounded-full font-bold px-5 py-2 text-sm" style={{ background: form.button_bg, color: form.button_text }}>{form.button_label}</span>
                )}
                {form.secondary_label && (
                  <span className="inline-block rounded-full font-bold px-5 py-2 text-sm border-2" style={{ borderColor: form.text_color, color: form.text_color }}>{form.secondary_label}</span>
                )}
              </div>
            </div>
          </div>

          {/* Enabled */}
          <div className="flex items-center justify-between rounded-2xl bg-brand-bg border border-brand-border p-4">
            <div>
              <p className="font-bold text-brand-text">Banner ativo</p>
              <p className="text-xs text-brand-textMuted">Desligue para esconder este banner do site.</p>
            </div>
            <Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="banner-enabled" />
          </div>

          {/* Texts */}
          <fieldset className="space-y-3">
            <legend className="font-heading font-bold text-lg text-brand-text">Textos</legend>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-bold text-brand-text mb-1">Selo (opcional)</label>
                <Input data-testid="banner-badge" value={form.badge} onChange={(e) => set("badge", e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
              </div>
              <div>
                <label className="block text-sm font-bold text-brand-text mb-1">Palavra em destaque</label>
                <Input data-testid="banner-highlight" value={form.highlight} onChange={(e) => set("highlight", e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl h-11" placeholder="ex: sem medo" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-brand-text mb-1">Título</label>
              <Input data-testid="banner-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
            </div>
            <div>
              <label className="block text-sm font-bold text-brand-text mb-1">Descrição</label>
              <Textarea data-testid="banner-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl" />
            </div>
          </fieldset>

          {/* Buttons */}
          <fieldset className="space-y-3">
            <legend className="font-heading font-bold text-lg text-brand-text">Botões</legend>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-bold text-brand-text mb-1">Texto do botão principal</label>
                <Input data-testid="banner-btn-label" value={form.button_label} onChange={(e) => set("button_label", e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
              </div>
              <div>
                <label className="block text-sm font-bold text-brand-text mb-1">Link do botão principal</label>
                <Input data-testid="banner-btn-link" value={form.button_link} onChange={(e) => set("button_link", e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl h-11" placeholder="https:// ou /caminho" />
              </div>
              <div>
                <label className="block text-sm font-bold text-brand-text mb-1">Texto do botão secundário</label>
                <Input data-testid="banner-secondary-label" value={form.secondary_label} onChange={(e) => set("secondary_label", e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
              </div>
              <div>
                <label className="block text-sm font-bold text-brand-text mb-1">Link do botão secundário</label>
                <Input data-testid="banner-secondary-link" value={form.secondary_link} onChange={(e) => set("secondary_link", e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
              </div>
            </div>
          </fieldset>

          {/* Colors */}
          <fieldset className="space-y-3">
            <legend className="font-heading font-bold text-lg text-brand-text inline-flex items-center gap-2"><Palette className="w-5 h-5" /> Cores</legend>
            <div className="grid sm:grid-cols-3 gap-3">
              <ColorField label="Fundo" value={form.bg_color} onChange={(v) => set("bg_color", v)} testId="color-bg" />
              <ColorField label="Texto" value={form.text_color} onChange={(v) => set("text_color", v)} testId="color-text" />
              <ColorField label="Destaque" value={form.accent_color} onChange={(v) => set("accent_color", v)} testId="color-accent" />
              <ColorField label="Fundo do botão" value={form.button_bg} onChange={(v) => set("button_bg", v)} testId="color-btn-bg" />
              <ColorField label="Texto do botão" value={form.button_text} onChange={(v) => set("button_text", v)} testId="color-btn-text" />
            </div>
          </fieldset>

          {/* Image */}
          <fieldset className="space-y-2">
            <legend className="font-heading font-bold text-lg text-brand-text">Imagem de fundo (opcional)</legend>
            <div className="flex items-center gap-4">
              {form.image_path && <img src={fileUrl(form.image_path)} alt="preview" className="w-24 h-24 object-cover rounded-2xl border border-brand-border" />}
              <label className="flex-1 flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-brand-border bg-brand-bg p-4 cursor-pointer hover:border-brand-primary transition-colors">
                <ImagePlus className="w-5 h-5 text-brand-primary" />
                <span className="font-bold text-brand-text text-sm">{uploading ? "Enviando..." : form.image_path ? "Trocar imagem" : "Escolher imagem"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="banner-image-input" />
              </label>
              {form.image_path && (
                <Button type="button" variant="outline" onClick={() => set("image_path", null)} className="rounded-full font-bold" data-testid="banner-image-clear">Remover</Button>
              )}
            </div>
          </fieldset>

          {/* Placement (only for custom banners) */}
          {!banner.built_in && (
            <fieldset className="space-y-2">
              <legend className="font-heading font-bold text-lg text-brand-text">Posição no site</legend>
              <select data-testid="banner-placement" value={form.placement || "home_middle"} onChange={(e) => set("placement", e.target.value)} className="w-full bg-brand-bg border border-brand-border rounded-2xl h-11 px-3 font-bold">
                {PLACEMENTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </fieldset>
          )}

          <DialogFooter className="gap-2 sticky bottom-0 bg-white pt-4 border-t border-brand-border">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full font-bold">Cancelar</Button>
            <Button type="submit" data-testid="banner-save-btn" disabled={saving || uploading} className="bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full font-bold">
              {saving ? "Salvando..." : "Salvar banner"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
